import { useCallback, useMemo } from 'react'
import type { Dispatch } from 'react'
import type { ReactionType, FeedbackType } from '@/types'
import type { DemoAction } from './demo-reducer'
import type { DemoEventSettings, DemoTimer, DemoNote } from './demo-types'
import { DEMO_SONGS } from './demo-data'

export function useDemoActions(dispatch: Dispatch<DemoAction>) {
  // Lifecycle
  const goLive = useCallback(() => dispatch({ type: 'GO_LIVE' }), [dispatch])

  // Audience actions
  const requestSong = useCallback(
    (songId: string, tipCents: number = 0, isFromVisitor: boolean = true, visitorName?: string) =>
      dispatch({ type: 'REQUEST_SONG', songId, tipCents, isFromVisitor, visitorName }),
    [dispatch]
  )
  const upvoteSong = useCallback(
    (requestId: string, isFromVisitor: boolean = true) =>
      dispatch({ type: 'UPVOTE_SONG', requestId, isFromVisitor }),
    [dispatch]
  )
  const addTipToRequest = useCallback(
    (requestId: string, tipCents: number, isFromVisitor: boolean = true) =>
      dispatch({ type: 'ADD_TIP_TO_REQUEST', requestId, tipCents, isFromVisitor }),
    [dispatch]
  )
  const cancelBoost = useCallback(
    (requestId: string) => dispatch({ type: 'CANCEL_BOOST', requestId }),
    [dispatch]
  )
  const sendDirectTip = useCallback(
    (tipCents: number, isFromVisitor: boolean = true) =>
      dispatch({ type: 'SEND_DIRECT_TIP', tipCents, isFromVisitor }),
    [dispatch]
  )
  const sendReaction = useCallback(
    (type: ReactionType) => dispatch({ type: 'SEND_REACTION', reactionType: type }),
    [dispatch]
  )
  const sendFeedback = useCallback(
    (type: FeedbackType, message?: string) =>
      dispatch({ type: 'SEND_FEEDBACK', feedbackType: type, message }),
    [dispatch]
  )

  // Artist actions
  const playSong = useCallback(
    (songId: string) => dispatch({ type: 'PLAY_SONG', songId }),
    [dispatch]
  )
  const playFromQueue = useCallback(
    (requestId: string) => dispatch({ type: 'PLAY_FROM_QUEUE', requestId }),
    [dispatch]
  )
  const endCurrentSong = useCallback(
    () => dispatch({ type: 'END_CURRENT_SONG' }),
    [dispatch]
  )
  const skipRequest = useCallback(
    (requestId: string) => dispatch({ type: 'SKIP_REQUEST', requestId }),
    [dispatch]
  )
  const squelchSong = useCallback(
    (songId: string, durationMinutes: number) =>
      dispatch({ type: 'SQUELCH_SONG', songId, durationMinutes }),
    [dispatch]
  )
  const unsquelchSong = useCallback(
    (songId: string) => dispatch({ type: 'UNSQUELCH_SONG', songId }),
    [dispatch]
  )
  const toggleSetting = useCallback(
    (key: keyof DemoEventSettings) => dispatch({ type: 'TOGGLE_SETTING', key }),
    [dispatch]
  )
  const updateTipAmounts = useCallback(
    (amounts: number[]) => dispatch({ type: 'UPDATE_TIP_AMOUNTS', amounts }),
    [dispatch]
  )
  const startBreak = useCallback(
    (durationMinutes: number) => dispatch({ type: 'START_BREAK', durationMinutes }),
    [dispatch]
  )
  const endBreak = useCallback(
    () => dispatch({ type: 'END_BREAK' }),
    [dispatch]
  )

  // Timer actions
  const startTimer = useCallback(
    (timerType: DemoTimer['type'], durationMinutes: number, label: string, isPublic: boolean) =>
      dispatch({ type: 'START_TIMER', timerType, durationMinutes, label, isPublic }),
    [dispatch]
  )
  const cancelTimer = useCallback(
    (timerId: string) => dispatch({ type: 'CANCEL_TIMER', timerId }),
    [dispatch]
  )
  const extendTimer = useCallback(
    (timerId: string, additionalMinutes: number) =>
      dispatch({ type: 'EXTEND_TIMER', timerId, additionalMinutes }),
    [dispatch]
  )
  const editTimer = useCallback(
    (timerId: string, updates: { label?: string; isPublic?: boolean }) =>
      dispatch({ type: 'EDIT_TIMER', timerId, updates }),
    [dispatch]
  )

  // Note actions
  const createNote = useCallback(
    (content: string, category: DemoNote['category'], linkToCurrentSong: boolean = false) =>
      dispatch({ type: 'CREATE_NOTE', content, category, linkToCurrentSong }),
    [dispatch]
  )
  const updateNote = useCallback(
    (noteId: string, content: string) => dispatch({ type: 'UPDATE_NOTE', noteId, content }),
    [dispatch]
  )
  const deleteNote = useCallback(
    (noteId: string) => dispatch({ type: 'DELETE_NOTE', noteId }),
    [dispatch]
  )

  // Setlist actions
  const activateSetlist = useCallback(
    (setlistId: string) => dispatch({ type: 'ACTIVATE_SETLIST', setlistId }),
    [dispatch]
  )
  const clearSetlist = useCallback(
    () => dispatch({ type: 'CLEAR_SETLIST' }),
    [dispatch]
  )
  const playFromSetlist = useCallback(
    (position: number) => dispatch({ type: 'PLAY_FROM_SETLIST', position }),
    [dispatch]
  )

  // Simulation controls
  const setVisitorCount = useCallback(
    (count: number) => dispatch({ type: 'SET_VISITOR_COUNT', count }),
    [dispatch]
  )
  const addSimulatedRequest = useCallback(
    () => dispatch({ type: 'ADD_SIMULATED_REQUEST' }),
    [dispatch]
  )
  const addSimulatedUpvote = useCallback(
    () => dispatch({ type: 'ADD_SIMULATED_UPVOTE' }),
    [dispatch]
  )
  const addSimulatedBoost = useCallback(
    () => dispatch({ type: 'ADD_SIMULATED_BOOST' }),
    [dispatch]
  )
  const addSimulatedTip = useCallback(
    () => dispatch({ type: 'ADD_SIMULATED_TIP' }),
    [dispatch]
  )
  const addSimulatedReaction = useCallback(
    () => dispatch({ type: 'ADD_SIMULATED_REACTION' }),
    [dispatch]
  )
  const addSimulatedFeedback = useCallback(
    () => dispatch({ type: 'ADD_SIMULATED_FEEDBACK' }),
    [dispatch]
  )

  // Utility functions
  const getSongById = useCallback(
    (songId: string) => DEMO_SONGS.find(s => s.id === songId),
    []
  )

  return useMemo(() => ({
    goLive,
    requestSong,
    upvoteSong,
    addTipToRequest,
    cancelBoost,
    sendDirectTip,
    sendReaction,
    sendFeedback,
    playSong,
    playFromQueue,
    endCurrentSong,
    skipRequest,
    squelchSong,
    unsquelchSong,
    toggleSetting,
    updateTipAmounts,
    startBreak,
    endBreak,
    startTimer,
    cancelTimer,
    extendTimer,
    editTimer,
    createNote,
    updateNote,
    deleteNote,
    activateSetlist,
    clearSetlist,
    playFromSetlist,
    setVisitorCount,
    addSimulatedRequest,
    addSimulatedUpvote,
    addSimulatedBoost,
    addSimulatedTip,
    addSimulatedReaction,
    addSimulatedFeedback,
    getSongById,
  }), [
    goLive, requestSong, upvoteSong, addTipToRequest, cancelBoost,
    sendDirectTip, sendReaction, sendFeedback, playSong, playFromQueue,
    endCurrentSong, skipRequest, squelchSong, unsquelchSong, toggleSetting,
    updateTipAmounts, startBreak, endBreak, startTimer, cancelTimer,
    extendTimer, editTimer, createNote, updateNote, deleteNote,
    activateSetlist, clearSetlist, playFromSetlist, setVisitorCount,
    addSimulatedRequest, addSimulatedUpvote, addSimulatedBoost,
    addSimulatedTip, addSimulatedReaction, addSimulatedFeedback, getSongById,
  ])
}
